<template>
    <div class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" aria-hidden="true"></div>
  
        <!-- Modal panel -->
        <div class="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <!-- Current Rate Banner -->
          <div class="px-6 py-6 bg-gradient-to-r from-sky-50 to-white border-b border-gray-200">
            <div class="flex items-center justify-between">
              <div>
                <h4 class="text-sm font-medium text-gray-500">Current Rate</h4>
                <div class="flex items-center mt-1 space-x-3">
                  <p class="text-2xl font-bold text-gray-900">{{ formatCurrentRate }}</p>
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800">
                    {{ formatRateType(currentRate.type) }}
                  </span>
                </div>
              </div>
              <div class="flex-shrink-0">
                <div class="p-2 bg-sky-50 rounded-full">
                  <svg class="w-6 h-6 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div class="px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <!-- Icon -->
              <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-sky-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                <svg class="w-6 h-6 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
  
              <!-- Content -->
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 class="text-lg font-medium leading-6 text-gray-900" id="modal-title">
                  Job-Specific Rate
                </h3>
                <div class="mt-2">
                  <div class="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p class="text-sm text-gray-600">
                      While your profile rate serves as your default rate, you can set a different rate specifically for this job opportunity. This allows you to adjust your rate based on the job's unique requirements and scope.
                    </p>
                  </div>
  
                  <!-- Rate Input -->
                  <div class="mt-6 space-y-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700">Rate Amount</label>
                      <div class="mt-1 relative rounded-md shadow-sm">
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span class="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          v-model="customRate"
                          @blur="validateRate"
                          class="focus:ring-sky-500 focus:border-sky-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                          :class="{'border-red-300': rateError}"
                          required
                        />
                      </div>
                      <p v-if="rateError" class="mt-2 text-sm text-red-600">{{ rateError }}</p>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700">Rate Type</label>
                      <select 
                        v-model="rateType"
                        class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm rounded-md"
                      >
                        <option value="hour">Hourly Rate</option>
                        <option value="day">Daily Rate</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          <!-- Action Buttons -->
          <div class="px-4 py-3 bg-gray-50 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              @click="confirmRate"
              :disabled="!isValidRate"
              :class="[
                'inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white border border-transparent rounded-md shadow-sm sm:ml-3 sm:w-auto sm:text-sm',
                isValidRate ? 'bg-sky-600 hover:bg-sky-700' : 'bg-gray-400 cursor-not-allowed'
              ]"
            >
              Apply with Selected Rate
            </button>
            <button
              type="button"
              @click="$emit('close')"
              class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'ReviseRateModal',
    props: {
      currentRate: {
        type: Object,
        required: true,
        default: () => ({
          rate: null,
          type: 'hour'
        })
      }
    },
    data() {
      return {
        customRate: null,
        rateType: 'hour',
        rateError: null
      }
    },
    computed: {
      formatCurrentRate() {
        if (!this.currentRate.rate) return 'Not specified'
        return `$${this.currentRate.rate}`
      },
      isValidRate() {
        return this.customRate && this.customRate > 0 && !this.rateError;
      }
    },
    methods: {
      formatRateType(type) {
        return type === 'hour' ? 'hourly' : 'daily'
      },
      validateRate() {
        if (!this.customRate || this.customRate === '') {
          this.rateError = 'Rate amount is required';
          return false;
        }
        if (this.customRate <= 0) {
          this.rateError = 'Rate must be greater than 0';
          return false;
        }
        this.rateError = null;
        return true;
      },
      confirmRate() {
        if (!this.validateRate()) {
          return;
        }

        const rateData = {
          rate: this.customRate,
          rate_type: this.rateType
        }
        this.$emit('rate-updated', rateData)
        this.$emit('close')
      }
    },
    created() {
      // Initialize with current rate values
      this.customRate = this.currentRate.rate || null
      this.rateType = this.currentRate.type || 'hour'
    }
  }
  </script> 